import React from "react";
import {Alert, Card, Col, Form, Row, Table} from "react-bootstrap";
import {WEEKDAYS, formatGermanDate} from "./scheduleForecast.js";

/**
 * Schritt 4 des Kampagnen-Assistenten: Zeitplan (Start/Ende, Standard-
 * Zeitfenster, Wochentags-Zeitfenster, Gesamtmenge) plus die ECHTE
 * Versandplan-Vorschau aus CampaignSchedulePlanner::preview() (vom
 * Controller als Prop `schedulePreview` übergeben - siehe
 * CampaignController::edit()). Anders als die frühere reine Klartext-
 * Schätzung berücksichtigt das earliest_send_at je Empfänger und ein
 * eventuelles total_to_send-Limit. Die Vorschau spiegelt den zuletzt
 * gespeicherten Stand wider - nach "Weiter"/"Speichern" wird die Seite neu
 * geladen und die Vorschau damit aktualisiert.
 */
export default function StepZeitplan({data, errors, handleChange, toggleWeekday, updateDayWindow, recipientsCount, schedulePreview}) {
    const proTagEntries = Object.entries(schedulePreview?.pro_tag ?? {});
    const hasPreview = !!schedulePreview && proTagEntries.length > 0;
    const nichtPlanbar = schedulePreview?.nicht_planbar ?? 0;
    const ungeplantWegenLimit = schedulePreview?.ungeplant_wegen_limit ?? 0;

    return (
        <Card>
            <Card.Body>
                <p className="text-muted">
                    Hier legst du fest, in welchem Zeitraum und zu welchen
                    Uhrzeiten versendet werden darf. Ein Wochentag ohne eigenes
                    Zeitfenster nutzt automatisch das Standard-Zeitfenster.
                </p>

                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={2}>Start-Datum</Form.Label>
                    <Col sm={4}>
                        <Form.Control type="date" id="send_start_date" value={data.send_start_date ?? ""}
                                      onChange={handleChange}/>
                    </Col>
                    <Form.Label column sm={2}>End-Datum</Form.Label>
                    <Col sm={4}>
                        <Form.Control type="date" id="send_end_date" value={data.send_end_date ?? ""}
                                      onChange={handleChange}
                                      className={errors.send_end_date ? "is-invalid" : ""}/>
                        {errors.send_end_date && <div className="invalid-feedback d-block">{errors.send_end_date}</div>}
                    </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={2}>Gesamtmenge</Form.Label>
                    <Col sm={4}>
                        <Form.Control type="number" min={0} id="total_to_send" value={data.total_to_send ?? ""}
                                      onChange={handleChange}/>
                        <Form.Text className="text-muted">
                            Optionale Obergrenze. Leer lassen, um alle importierten Empfänger zu versenden.
                        </Form.Text>
                    </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={2}>Standard-Zeitfenster</Form.Label>
                    <Col sm={4}>
                        <Form.Control type="time" id="send_window_start_time"
                                      value={data.send_window_start_time ?? ""} onChange={handleChange}/>
                    </Col>
                    <Form.Label column sm={2}>bis</Form.Label>
                    <Col sm={4}>
                        <Form.Control type="time" id="send_window_end_time"
                                      value={data.send_window_end_time ?? ""} onChange={handleChange}/>
                    </Col>
                    <Col sm={{span: 10, offset: 2}}>
                        <Form.Text className="text-muted">
                            Gilt für alle aktiven Wochentage ohne eigenes Zeitfenster (siehe unten).
                        </Form.Text>
                    </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={2}>Wochentage</Form.Label>
                    <Col sm={10}>
                        <table className="table table-sm align-middle w-auto mb-1">
                            <thead>
                                <tr>
                                    <th>Tag</th>
                                    <th>von</th>
                                    <th>bis</th>
                                </tr>
                            </thead>
                            <tbody>
                                {WEEKDAYS.map((day) => {
                                    const active = data.send_weekdays.includes(day.value);
                                    const dayWindow = data.send_day_windows[day.value] ?? {};

                                    return (
                                        <tr key={day.value}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`weekday-${day.value}`}
                                                    label={day.label}
                                                    checked={active}
                                                    onChange={() => toggleWeekday(day.value)}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    disabled={!active}
                                                    value={dayWindow.start ?? ""}
                                                    onChange={(e) => updateDayWindow(day.value, "start", e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    disabled={!active}
                                                    value={dayWindow.end ?? ""}
                                                    onChange={(e) => updateDayWindow(day.value, "end", e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {errors.send_day_windows && (
                            <div className="text-danger">{errors.send_day_windows}</div>
                        )}
                    </Col>
                </Form.Group>

                {!schedulePreview && (
                    <Alert variant="secondary" className="mb-0">
                        Der Versandplan wird berechnet, sobald die Kampagne gespeichert ist und Empfänger importiert wurden.
                    </Alert>
                )}

                {schedulePreview && !hasPreview && (
                    <Alert variant="warning" className="mb-0">
                        Mit den aktuell gespeicherten Angaben (Zeitraum, Wochentage, Empfänger) konnte kein
                        Versandplan berechnet werden. Bitte Zeitraum, Wochentage und Empfänger prüfen.
                    </Alert>
                )}

                {hasPreview && (
                    <>
                        <Alert variant="info" className="mb-3">
                            <strong>Versandplan:</strong> {schedulePreview.geplant} Mail(s) eingeplant
                            {schedulePreview.erster && schedulePreview.letzter && (
                                <> - erste am {formatGermanDate(proTagEntries[0][0])}, letzte am {formatGermanDate(proTagEntries[proTagEntries.length - 1][0])}.</>
                            )}
                        </Alert>

                        {(nichtPlanbar > 0 || ungeplantWegenLimit > 0) && (
                            <Alert variant="warning">
                                {nichtPlanbar > 0 && (
                                    <div>
                                        <strong>{nichtPlanbar}</strong> Empfänger passen mit dem aktuellen Zeitraum
                                        (Start-/End-Datum, Wochentage) in keinen Versandtag mehr - der Zeitraum ist
                                        zu kurz oder die Wochentage zu wenige.
                                    </div>
                                )}
                                {ungeplantWegenLimit > 0 && (
                                    <div>
                                        <strong>{ungeplantWegenLimit}</strong> Empfänger bleiben wegen der
                                        Gesamtmenge-Obergrenze unversendet.
                                    </div>
                                )}
                            </Alert>
                        )}

                        <Table size="sm" bordered className="mb-0">
                            <thead>
                                <tr>
                                    <th>Tag</th>
                                    <th>Anzahl</th>
                                    <th>von</th>
                                    <th>bis</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proTagEntries.map(([day, entry]) => (
                                    <tr key={day}>
                                        <td>{formatGermanDate(day)}</td>
                                        <td>{entry.anzahl}</td>
                                        <td>{entry.von}</td>
                                        <td>{entry.bis}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
